// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * `.env` parsing.
 *
 * TWO PARSERS RUN AND THE UNION OF THEIR RESULTS BECOMES THE NEEDLE SET.
 *
 * This is not belt-and-braces, it is a correctness requirement. The spec assumed `dotenv` is the
 * parser. Vite 8 — the current `latest` — does not use it: `vite/dist/node/chunks/node.js` does
 * `import { parseEnv } from "node:util"` and `loadEnv` calls `parseEnv(readFileSync(file,'utf-8'))`.
 * The two disagree on real input, and `parseEnv` has a value-losing bug: on node v22,
 * `"A=   \nB=b\n"` gives dotenv `{A:"", B:"b"}` but parseEnv `{A:"B=b"}` — a whitespace-only value
 * makes it swallow the following line, fabricating one needle and destroying another.
 *
 * A superfluous needle costs one pattern in an automaton whose cost is flat. A missing needle is a
 * silent false-clean in a security tool. So: union, never intersection.
 */

export interface ParsedEnv {
  readonly entries: Map<string, string>;
  readonly parser: 'dotenv' | 'parseEnv';
}

/**
 * The dotenv `LINE` regex, byte-identical across dotenv 16.6.1 and 17.4.2, and vendored verbatim by
 * `@next/env` 16.3.0. Reproduced here rather than depending on dotenv, because we need the grammar
 * in a browser and we need to run it beside a second parser that disagrees with it.
 */
const DOTENV_LINE =
  /(?:^|^)\s*(?:export\s+)?([\w.-]+)(?:\s*=\s*?|:\s+?)(\s*'(?:\\'|[^'])*'|\s*"(?:\\"|[^"])*"|\s*`(?:\\`|[^`])*`|[^#\r\n]+)?\s*(?:#.*)?(?:$|$)/gm;

function stripBom(src: string): string {
  return src.charCodeAt(0) === 0xfeff ? src.slice(1) : src;
}

/** Parser 1 — the dotenv grammar. */
export function parseDotenv(source: string): Map<string, string> {
  const out = new Map<string, string>();
  const src = stripBom(source).replace(/\r\n?/g, '\n');
  DOTENV_LINE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = DOTENV_LINE.exec(src)) !== null) {
    // A zero-length match on an empty line would spin forever.
    if (m[0].length === 0) {
      DOTENV_LINE.lastIndex += 1;
      continue;
    }
    const key = m[1];
    if (!key) continue;
    let value = m[2] ?? '';

    // The value is trimmed BEFORE quotes are stripped. `K=  v  ` -> `v`, but `K='  v  '` keeps its
    // interior spaces. Getting this order wrong builds a needle with the wrong boundaries.
    value = value.trim();

    const first = value[0];
    const last = value[value.length - 1];
    const quoted = value.length >= 2 && (first === '"' || first === "'" || first === '`') && last === first;
    if (quoted) {
      const quote = first;
      value = value.slice(1, -1);
      // ONLY \n and \r are unescaped, and ONLY inside double quotes. `"a\tb"` keeps a literal
      // backslash-t; `"he said \"hi\""` keeps its backslashes, so the runtime value contains `\"`.
      // This is the single most likely place to construct the wrong needle.
      if (quote === '"') value = value.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
    }
    out.set(key, value);
  }
  return out;
}

/**
 * Parser 2 — a reimplementation of `node:util.parseEnv`, which is what Vite 8 actually runs.
 *
 * The three divergences that matter, all measured against node v22.22.1:
 *   1. `K: v` (colon separator) is dropped entirely.
 *   2. `"he said \"hi\""` truncates to `he said \`.
 *   3. A whitespace-only value swallows the next line: `A=   \nB=b` -> `{A: "B=b"}`.
 *
 * We reproduce (1) and (3) — the two that change the needle set — and approximate (2). Reproducing
 * the bug is the point: it is what Vite will have done to the user's file.
 */
export function parseEnvCompat(source: string): Map<string, string> {
  const out = new Map<string, string>();
  const src = stripBom(source).replace(/\r\n?/g, '\n');
  let i = 0;
  const n = src.length;

  const isSpace = (c: string) => c === ' ' || c === '\t';

  while (i < n) {
    // Skip leading whitespace and blank lines.
    while (i < n && (isSpace(src[i]!) || src[i] === '\n')) i++;
    if (i >= n) break;
    if (src[i] === '#') {
      while (i < n && src[i] !== '\n') i++;
      continue;
    }
    const keyStart = i;
    while (i < n && src[i] !== '=' && src[i] !== '\n') i++;
    if (i >= n || src[i] === '\n') {
      // No `=` on this line. parseEnv has no colon separator at all, so `K: v` lands here and is
      // dropped — divergence (1).
      continue;
    }
    const key = src.slice(keyStart, i).trim();
    i++; // past '='

    // Divergence (3): parseEnv does not stop at the newline when the value is whitespace-only. It
    // skips whitespace INCLUDING newlines looking for the value, so the next line becomes it.
    while (i < n && (isSpace(src[i]!) || src[i] === '\n')) i++;

    let value = '';
    const q = src[i];
    if (q === '"' || q === "'" || q === '`') {
      i++;
      const start = i;
      while (i < n && src[i] !== q) i++;
      value = src.slice(start, i);
      if (i < n) i++;
      if (q === '"') value = value.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
    } else {
      const start = i;
      while (i < n && src[i] !== '\n') i++;
      value = src.slice(start, i).trim();
      const hash = value.indexOf('#');
      if (hash >= 0) value = value.slice(0, hash).trimEnd();
    }
    if (key) out.set(key, value);
  }
  return out;
}

export interface UnionEntry {
  readonly key: string;
  readonly value: string;
  readonly parsers: ('dotenv' | 'parseEnv')[];
}

/**
 * The union. Every distinct (key, value) from either parser is a candidate. Where the two disagree
 * on a key, BOTH values are enrolled and the UI shows a "your bundler's parser may read this line
 * differently" row.
 */
export function parseEnvUnion(source: string): UnionEntry[] {
  const a = parseDotenv(source);
  const b = parseEnvCompat(source);
  const byKey = new Map<string, Map<string, ('dotenv' | 'parseEnv')[]>>();

  const add = (k: string, v: string, p: 'dotenv' | 'parseEnv') => {
    let m = byKey.get(k);
    if (!m) byKey.set(k, (m = new Map()));
    const list = m.get(v);
    if (list) list.push(p);
    else m.set(v, [p]);
  };
  for (const [k, v] of a) add(k, v, 'dotenv');
  for (const [k, v] of b) add(k, v, 'parseEnv');

  const out: UnionEntry[] = [];
  for (const [key, values] of byKey) {
    for (const [value, parsers] of values) out.push({ key, value, parsers });
  }
  return out;
}

/**
 * Vite's `.env` precedence, lowest to highest. `.env.[mode].local` wins overall.
 *
 * The spec said "read .env / .env.local". Measured: with `.env` and `.env.production.local` both
 * present, the value in the bundle came from `.env.production.local`. A tool reading only one file
 * reports a false negative on the value that actually shipped AND a false positive on the stale one.
 */
export function envPrecedence(mode: string): string[] {
  return ['.env', '.env.local', `.env.${mode}`, `.env.${mode}.local`];
}

export interface ResolvedEnv {
  readonly key: string;
  readonly value: string;
  readonly source: string;
  readonly parsers: ('dotenv' | 'parseEnv')[];
  /** Other values for this key that also entered the needle set. */
  readonly alternates: { value: string; source: string; why: string }[];
}

/**
 * Resolve a set of `.env.*` files into one table. Every value that any parser produced for a key
 * from any file stays enrolled as an alternate — the resolved value is what we SHOW, the alternates
 * are what we also SEARCH, because being wrong about precedence must not cause a false clean.
 */
export function resolveEnvFiles(files: { name: string; content: string }[], mode: string): ResolvedEnv[] {
  const order = envPrecedence(mode);
  const rank = (name: string) => {
    const i = order.indexOf(name);
    return i < 0 ? -1 : i;
  };
  const winners = new Map<string, ResolvedEnv>();

  for (const file of files) {
    const r = rank(file.name);
    if (r < 0) continue;
    for (const e of parseEnvUnion(file.content)) {
      const prev = winners.get(e.key);
      if (!prev) {
        winners.set(e.key, { key: e.key, value: e.value, source: file.name, parsers: e.parsers, alternates: [] });
        continue;
      }
      if (prev.source === file.name) {
        // Same file, second parser disagreed.
        if (prev.value !== e.value) {
          prev.alternates.push({ value: e.value, source: file.name, why: `${e.parsers.join('/')} reads this line differently` });
        }
        continue;
      }
      const prevRank = rank(prev.source);
      if (r >= prevRank) {
        const demoted = { value: prev.value, source: prev.source, why: `overridden by ${file.name}` };
        const merged: ResolvedEnv = {
          key: e.key,
          value: e.value,
          source: file.name,
          parsers: e.parsers,
          alternates: [...prev.alternates, ...(prev.value === e.value ? [] : [demoted])],
        };
        winners.set(e.key, merged);
      } else if (prev.value !== e.value) {
        prev.alternates.push({ value: e.value, source: file.name, why: `lower precedence than ${prev.source}` });
      }
    }
  }
  return [...winners.values()];
}
